import { AuthKeyStatusValues } from "@/api/useAuthKeys.selector"
import { AuthKeysResponse, AuthKeyStatus } from "@/api/useAuthKeys.types"
import { MONTH_IN_SECONDS } from "@/const/default-values"
import {
  getDateFromSeconds,
  getDateInUserLocale,
  getUnixDate
} from "@/helpers/date.helpers"
import {
  getInputFilter,
  getMultipleValuesFilter
} from "@/helpers/filters.helper"

import type { FilterAuthKeysResponse } from "@/api/useAuthKeys.types"
import type { TFunction } from "@/hooks/useTranslation"

export const getAuthKeysData = (response: AuthKeysResponse) => {
  const { data, metadata } = response || {}

  const getDateOrDefault = (date: number) =>
    getDateInUserLocale(getDateFromSeconds(date), "lll") || "-"

  return {
    metadata,
    data: data?.map((key) => {
      return {
        id: key.id,
        key: key.id,
        name: key.name,
        description: key.description,
        creator: key.createdBy ?? { name: "Unknown", email: "Unknown" },
        createdAt: getDateOrDefault(key.createdAt),
        expiry: getDateOrDefault(key.expiry),
        status: getExpiryValue(key.expiry),
        lastUsedAt: getDateOrDefault(key.lastUsedAt)
      }
    })
  }
}

function getExpiryValue(expiry: number): AuthKeyStatus {
  if (expiry === 0) {
    return AuthKeyStatusValues.ACTIVE
  }

  const now = getUnixDate()
  const untilExpiry = expiry - now

  if (untilExpiry <= 0) {
    return AuthKeyStatusValues.EXPIRED
  }
  if (untilExpiry < MONTH_IN_SECONDS) {
    return AuthKeyStatusValues.EXPIRING_SOON
  }
  return AuthKeyStatusValues.ACTIVE
}

export const getFiltersKeys = () => [
  "name",
  "description",
  "status",
  "createdBy"
]

export const getFiltersAuthKeysSelector = (
  response: FilterAuthKeysResponse,
  t: TFunction
) => {
  const { data, ...restResponse } = response || {}

  const status = getMultipleValuesFilter("status", t, {
    options: (data?.status || []).map((value) => ({
      label: t(`settings.authkeys.status.${value.toLowerCase()}`),
      value
    }))
  })

  const createdBy = getMultipleValuesFilter("createdBy", t, {
    options: (data?.createdBy || []).map(({ id, name, email }) => ({
      label: name.trim() || email,
      value: id
    }))
  })

  const filters = getFiltersKeys().map((key) => {
    if (key === "status") return status
    if (key === "createdBy") return createdBy

    return getInputFilter(key, t)
  })

  return {
    data: { status, createdBy, all: filters },
    ...restResponse
  }
}
